
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import CurrenciesStore from "@/mixins/constants/currenciesStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ActionTypes } from "@/data/enums";
import {
  ProvisionCategoryCodes,
  RegistrarTypes
} from "@/modules/web_hosting/data/enums";

// Types
import { IProvider } from "@/models/providers";
import {
  ITldProvider,
  ITldProviderMethods,
  ITldDefaultSettings
} from "@/modules/web_hosting/models/domains";
import {
  IProvisionProvider,
  IProvisionConfiguration,
  IProvisionCategory,
  IBlueprint
} from "@/models/provisioning";
import { IState } from "@/store";
import { IBrand } from "@/models/brands";
import { IProduct, IProductPriceRecalculation } from "@/models/products";

@Component({
  computed: {
    ...mapState({
      configurationsScope(state: IState, getters) {
        return getters[`data/${DataModules.PROVISIONING_CONFIGURATIONS}/scope`];
      },
      configurations(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_CONFIGURATIONS,
          scope: this.configurationsScope
        });
      },

      brandScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/brandScope`](this.brandId);
      },
      brand(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.BRANDS,
          scope: this.brandScope
        });
      },

      defaultTldSettingsScope(state: IState, getters) {
        return getters[
          `data/${DataModules.HOST_MODULE_TLDS}/defaultTldSettingsScope`
        ]();
      },
      defaultTldSettings(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.HOST_MODULE_TLDS,
          scope: this.defaultTldSettingsScope
        });
      },

      blueprintsScope(state: IState, getters) {
        return getters[`data/${DataModules.PROVISIONING_BLUEPRINTS}/scope`];
      },
      blueprints(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_BLUEPRINTS,
          scope: this.allBlueprintsScope
        });
      }
    })
  }
})
export default class TldProvider
  extends Mixins(RequestPromises, CurrenciesStore)
  implements IProvider {
  // Map state

  public configurationsScope!: string;
  public configurations!: IProvisionConfiguration[];

  public brandScope!: string;
  public brand!: IBrand;

  public defaultTldSettingsScope!: Function;
  public defaultTldSettings!: ITldDefaultSettings;

  public readonly blueprints!: IBlueprint[];
  public readonly blueprintsScope!: string;

  // Props

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: true })
  public handleGuarding!: boolean;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: null })
  public tldId!: IProduct["id"];

  @Prop({ required: true })
  public domainProviders!: IProvisionProvider[];

  @Prop({ required: true })
  public domainCategoryId!: IProvisionCategory["id"];

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.HOST_MODULE_TLDS,
      scope: this.defaultTldSettingsScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_CONFIGURATIONS,
      scope: this.configurationsScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BLUEPRINTS,
      scope: this.allBlueprintsScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getProvisionConfigurations(this.isReloading),
        this.getBrandDetails(this.isReloading),
        this.getBlueprints(this.isReloading),
        this.getCurrencies()
      ]);

      this.$emit("initiate");
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getProvisionConfigurations(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONFIGURATIONS}/list`,
      {
        scope: this.configurationsScope,
        params: {
          limit: 0,
          "filter[category.code]": ProvisionCategoryCodes.DOMAINS,
          "filter[enabled]": true
        },
        vm: this,
        ignoreStored
      }
    );
  }

  public getBrandDetails(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.BRANDS}/get`, {
      id: this.brandId,
      scope: this.brandScope,
      ignoreStored,
      vm: this
    });
  }

  public getTldDefaultSettings(tld: string) {
    return this.$store.dispatch(
      `data/${DataModules.HOST_MODULE_TLDS}/getTldDefaultSettings`,
      {
        tld,
        scope: this.defaultTldSettingsScope,
        vm: this,
        ignoreStored: true
      }
    );
  }

  public getBlueprints(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BLUEPRINTS}/list`,
      {
        scope: this.allBlueprintsScope,
        params: {
          with: "blueprint_field_counts",
          order: "name"
        },
        vm: this,
        ignoreStored
      }
    );
  }

  public createTld(data) {
    if (!this.canCreate) return;

    return this.$store.dispatch(`data/${DataModules.HOST_MODULE_TLDS}/create`, {
      data
    });
  }

  public updateTld(data) {
    if (!this.canUpdate) return;

    return this.$store.dispatch(`data/${DataModules.HOST_MODULE_TLDS}/update`, {
      id: this.tldId,
      data
    });
  }

  public openCreateConfigurationModal() {
    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONFIGURATIONS}/openManageProviderConfigurationModal`,
      {
        total: this.configurations.length,
        props: {
          categoryId: this.domainCategoryId,
          actionType: ActionTypes.CREATE
        },
        events: {
          success: this.reloadData
        }
      }
    );
  }

  public getProviderLogoUrl(configuration) {
    return _.get(
      _.find(this.domainProviders, ["id", configuration.provider_id]),
      "logo_url",
      ""
    );
  }

  // Computed

  public get brandId(): IBrand["id"] {
    return this.$store.state.brand.id;
  }

  public get availableConfigurations(): IProvisionConfiguration[] {
    return _.concat(
      _.filter(this.configurations, configuration => {
        return _.some(this.domainProviders, ["id", configuration.provider_id]);
      }),
      this.manualConfiguration
    ) as IProvisionConfiguration[];
  }

  public get manualBlueprint() {
    return _.find(this.blueprints, [
      "code",
      RegistrarTypes.MANUAL
    ]) as IBlueprint;
  }

  public get manualConfiguration(): IProvisionConfiguration[] {
    if (_.isEmpty(this.manualBlueprint)) return [];

    return [
      {
        name: this.$t("_.provision_manually").toString(),
        id: RegistrarTypes.MANUAL,
        icon: "tools"
      } as IProvisionConfiguration
    ];
  }

  public get allBlueprintsScope() {
    return `${this.blueprintsScope}_modal`;
  }

  public get multicurrencyOptions() {
    return [
      {
        leftLabel: this.$t("_.omit_product"),
        desc: this.$t("_sentence.omit_product_desc"),
        value: 0
      },
      {
        leftLabel: this.$t("_.make_available"),
        desc: this.$t("_sentence.make_available_desc", {
          currency: this.getCurrencyCode(this.brand?.currency_id)
        }),
        value: 1
      },
      {
        leftLabel: this.$t("_.inherit_brand_default_value", {
          value: this.$t("_.omit_product")
        }),
        value: 2
      }
    ] as IProductPriceRecalculation[];
  }

  public get brandPriceListId() {
    return _.get(this.brand, "pricelist_id");
  }

  // Guarding

  public get canCreate() {
    return this.$userCan("web_hosting::create_tld_product");
  }

  public get canUpdate() {
    return this.$userCan("web_hosting::update_tld_product");
  }

  public get canManagePrices() {
    return this.$userCan(
      "create_pricelist_product_prices",
      "delete_pricelist_product_prices",
      "sync_pricelist_product_prices",
      "update_product"
    );
  }

  public guardFunctionalities = [
    "web_hosting::get_tld_product_default_settings",
    "list_provision_configurations"
  ];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("tldProvider")
  public providerData(): ITldProvider {
    return {
      availableConfigurations: this.availableConfigurations,
      brandPriceListId: this.brandPriceListId,
      canCreate: this.canCreate,
      canManage: _.isNull(this.tldId) ? this.canCreate : this.canUpdate,
      canUpdate: this.canUpdate,
      contentIsAvailable: this.contentIsAvailable,
      createInvoiceTermBV: _.get(this.brand, "create_invoice_term"),
      defaultTldSettings: this.defaultTldSettings,
      guardFunctionalities: this.guardFunctionalities,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      manualBlueprint: this.manualBlueprint,
      multicurrencyOptions: this.multicurrencyOptions,
      paymentDaysTermBV: _.get(this.brand, "payment_days_term")
    };
  }

  @Provide("tldProviderMethods")
  public get providerMethods(): ITldProviderMethods {
    return {
      createTld: this.createTld,
      getProviderLogoUrl: this.getProviderLogoUrl,
      getTldDefaultSettings: this.getTldDefaultSettings,
      reloadData: this.reloadData,
      updateTld: this.updateTld
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false, ifClient: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $tldData: this.providerData(),
        $tldMethods: this.providerMethods
      })
    );
  }
}
